import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Clock, Check, Send, MessageSquare, MessageCircle, ShieldCheck } from 'lucide-react';

import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { submitForm } from '@/lib/crmForms';
import { track } from '@/lib/tracking';

import { ADDRESS_LINES, BRAND, SUPPORT_WHATSAPP, whatsappHref } from '@/data/constants';


import Seo from '@/components/Seo';
import Turnstile, { TurnstileStatus } from '@/components/Turnstile';
import { captchaPendingMessage } from '@/lib/authGuard';

/** Subject options -> CRM form type. 'support' routes to the Support Request stage. */
export const CONTACT_SUBJECTS = [
  { value: 'general', key: 'contact.subject.general', formType: 'contact' as const },
  { value: 'billing', key: 'contact.subject.billing', formType: 'support' as const },
  { value: 'support', key: 'contact.subject.support', formType: 'support' as const },
  { value: 'report', key: 'contact.subject.report', formType: 'support' as const },
];

const inputCls =
  'w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500';

const Contact: React.FC = () => {
  const { t, path, lang } = useI18n();
  const { profile } = useAuth();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  // Email-only contact form: no phone and no SMS consent are collected here.
  // Marketing-email consent stays opt-in (unchecked) and is never required.
  const [consent, setConsent] = useState(false);
  const [token, setToken] = useState('');
  // Single-use Turnstile token handling (same pattern as Register / Login).
  const [captchaStatus, setCaptchaStatus] = useState<TurnstileStatus>('loading');
  const [captchaReset, setCaptchaReset] = useState(0);
  const [subject, setSubject] = useState('general');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const [started, setStarted] = useState(false);

  // Prefill from the logged in profile the first time it becomes available
  React.useEffect(() => {
    if (!profile) return;
    setName((n) => n || profile.company_name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim());
    setEmail((e) => e || profile.email || '');
  }, [profile]);


  /** Anonymous funnel event — fired once when the visitor starts typing. */
  const markStarted = () => {
    if (started) return;
    setStarted(true);
    track('form_started', { form: 'contact' });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setErr(t('auth.errInvalid'));
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setErr(t('auth.errEmail'));
      return;
    }
    if (message.trim().length < 10) {
      setErr(t('contact.errMessage'));
      return;
    }
    // Do not submit until the widget has produced a valid token.
    const pendingMsg = captchaPendingMessage(captchaStatus, token, lang);
    if (pendingMsg) {
      setErr(pendingMsg);
      return;
    }
    setErr('');
    setSending(true);

    const chosen = CONTACT_SUBJECTS.find((s) => s.value === subject) || CONTACT_SUBJECTS[0];

    const { error } = await supabase.from('mkt_contact_messages').insert({
      user_id: profile?.id || null,
      name: name.trim(),
      email: email.trim().toLowerCase(),
      phone: null,
      subject,
      message: message.trim(),
      language: lang,
      status: 'new',
    });

    if (error) {
      setErr(t('contact.errSave'));
      setSending(false);
      return;
    }

    // Push the contact into GoHighLevel through the protected crm-capture
    // function (server-side Turnstile verification + rate limiting + consent).
    // Email-only form: no phone is collected and SMS consent is never recorded here.
    const res = await submitForm({
      formType: chosen.formType,
      email: email.trim().toLowerCase(),
      name: name.trim(),
      smsOptIn: false,
      consent,

      turnstileToken: token,
      language: lang,
      userType: profile?.user_type || 'lead',
      userId: profile?.id,
      tags: [`subject:${subject}`, `lang:${lang}`],
      fields: {
        subject,
        message: message.trim(),
        page: 'contact',
      },
    });
    // crm-capture has now consumed the single-use token (pass or fail).
    // Always fetch a fresh one so a retry or "send another" never re-sends it.
    setCaptchaReset((n) => n + 1);

    if (res && res.ok === false && (res.error === 'bot_check_failed' || res.error === 'rate_limited')) {
      // The message itself is already saved; tell the visitor what happened.
      setErr(res.message || t('sec.botFailed'));
      setSending(false);
      return;
    }

    await supabase.from('mkt_activity').insert({
      user_id: profile?.id || null,
      action: 'contact_message',
      detail: `${subject} — ${email.trim().toLowerCase()}`,
    });

    setSending(false);
    setDone(true);
  };


  const resetForm = () => {
    setDone(false);
    setMessage('');
    setSubject('general');
  };

  return (
    <div className="bg-slate-50">
      <Seo title={t('contact.title')} description={t('contact.sub')} />

      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-orange-600">
            <MessageSquare className="h-3.5 w-3.5" /> {t('nav.contact')}
          </span>
          <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            {t('contact.title')}
          </h1>
          <p className="mt-2 max-w-2xl text-base text-slate-600">{t('contact.sub')}</p>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-12 sm:px-6 lg:grid-cols-3 lg:px-8">
        <div className="lg:col-span-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
            {done ? (
              <div className="py-8 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
                  <Check className="h-7 w-7 text-emerald-600" />
                </span>
                <h2 className="mt-4 text-xl font-extrabold text-slate-900">{t('contact.successTitle')}</h2>
                <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">{t('contact.successBody')}</p>
                <button
                  onClick={resetForm}
                  className="mt-6 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-slate-800"
                >
                  {t('contact.sendAnother')}
                </button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate>
                <h2 className="text-lg font-extrabold text-slate-900">{t('contact.formTitle')}</h2>

                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <label className="text-sm font-semibold text-slate-700 sm:col-span-2">
                    {t('common.name')}
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      onFocus={markStarted}
                      className={`mt-1.5 ${inputCls}`}
                      placeholder={t('common.name')}
                    />
                  </label>


                  <label className="text-sm font-semibold text-slate-700 sm:col-span-2">
                    {t('common.email')}
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`mt-1.5 ${inputCls}`}
                      placeholder="name@company.com"
                    />
                  </label>


                  <label className="text-sm font-semibold text-slate-700 sm:col-span-2">
                    {t('contact.subject')}
                    <select
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className={`mt-1.5 ${inputCls}`}
                    >
                      {CONTACT_SUBJECTS.map((s) => (
                        <option key={s.value} value={s.value}>
                          {t(s.key)}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="text-sm font-semibold text-slate-700 sm:col-span-2">
                    {t('contact.message')}
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={6}
                      className={`mt-1.5 ${inputCls}`}
                      placeholder={t('contact.messagePlaceholder')}
                    />
                  </label>
                </div>

                {/* Contact is an email-only form. No phone field and no SMS
                    consent here — the GoHighLevel chat widget is the single
                    SMS/chat entry point (removes a duplicate SMS opt-in). */}


                {/* Explicit marketing consent — recorded with timestamp, form
                    name, language and policy version before anything is sent
                    to the CRM. Transactional replies are separate. */}
                <label className="mt-2 flex items-start gap-2 text-xs text-slate-500">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    {t('sec.consentMarketing')}{' '}
                    <Link to={path('/privacy')} className="font-semibold text-slate-700 underline">
                      {t('footer.privacy')}
                    </Link>{' '}
                    ·{' '}
                    <Link to={path('/terms')} className="font-semibold text-slate-700 underline">
                      {t('footer.terms')}
                    </Link>
                  </span>
                </label>

                <Turnstile
                  action="contact"
                  onVerify={setToken}
                  onStatus={setCaptchaStatus}
                  resetKey={captchaReset}
                  className="mt-4"
                  label={t('sec.humanCheck')}
                />

                {err && (
                  <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">{err}</p>
                )}


                <button
                  type="submit"
                  disabled={sending}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-orange-500 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-orange-600 disabled:opacity-60 sm:w-auto"
                >
                  <Send className="h-4 w-4" />
                  {sending ? t('contact.sending') : t('contact.send')}
                </button>

                <p className="mt-4 flex items-start gap-2 text-xs text-slate-500">
                  <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                  <span>
                    {t('contact.privacyNote')}{' '}
                    <Link to={path('/privacy')} className="font-semibold text-slate-700 underline">
                      {t('footer.privacy')}
                    </Link>
                  </span>
                </p>
              </form>
            )}
          </div>
        </div>

        <aside className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h2 className="text-sm font-extrabold uppercase tracking-wide text-slate-900">{t('contact.reachUs')}</h2>
            <ul className="mt-4 space-y-4 text-sm">
              <li>
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{t('contact.emailUs')}</p>
                <a
                  href={`mailto:${BRAND.email}`}
                  className="mt-1 flex items-center gap-2 font-semibold text-slate-800 hover:text-orange-600"
                >
                  <Mail className="h-4 w-4 text-orange-500" />
                  {BRAND.email}
                </a>
              </li>
              {/* No public support phone line is published. WhatsApp is the one
                  live channel we staff, so it is offered here as well as in the
                  floating action button (same SUPPORT_WHATSAPP source). */}
              <li>
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{t('cta.whatsapp')}</p>
                <a
                  href={whatsappHref(lang)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track('cta_click', { action: 'whatsapp', channel: 'contact-page' })}
                  className="mt-1 flex items-center gap-2 font-semibold text-slate-800 hover:text-emerald-600"
                >
                  <MessageCircle className="h-4 w-4 text-[#25D366]" />
                  {SUPPORT_WHATSAPP.display}
                </a>
              </li>


              <li>
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                  {t('contact.mailingAddress')}
                </p>
                <div className="mt-1 flex items-start gap-2">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />
                  <address className="not-italic font-semibold leading-relaxed text-slate-800">
                    <span className="block">{BRAND.fullName}</span>
                    {ADDRESS_LINES.map((line) => (
                      <span key={line} className="block">
                        {line}
                      </span>
                    ))}
                  </address>
                </div>
                <p className="mt-2 text-xs text-slate-500">{t('contact.addressNote')}</p>
              </li>

              <li>
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{t('contact.hours')}</p>
                <p className="mt-1 flex items-start gap-2 font-semibold text-slate-800">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />
                  {t('contact.hoursVal')}
                </p>
              </li>
            </ul>
          </div>

          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
            <p className="text-sm font-bold text-emerald-800">{t('contact.responseTime')}</p>
            <p className="mt-2 text-sm text-emerald-700">{t('contact.faqLink')}</p>
            <Link
              to={path('/how-it-works')}
              className="mt-3 inline-block rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700"
            >
              {t('home.faqTitle')}
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Contact;
